import React, { useState } from 'react';
import { Link, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { 
  House, 
  Broom, 
  Users, 
  Buildings,
  Calendar, 
  Clock,
  Question,
  List,
  X,
  UserCircle as UserCircleIcon
} from '@phosphor-icons/react';
import { APP_VERSION } from '../appVersion';
import './Layout.css';

function Layout({ children }) {
  const location = useLocation();
  const {
    user,
    realUser,
    hasRealSession,
    loading: authLoading,
    isAllowedUser,
    isSuperAdmin,
    activeEmployeeId,
    providerName,
    providerConfigured,
    authError,
    refreshAuth,
    switchEmployeeId,
    impersonatedEmail,
    signOutLocalDevelopment,
    logoutUrl,
  } = useAuth();
  
  const [inputEmployeeId, setInputEmployeeId] = useState(activeEmployeeId || '167');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  React.useEffect(() => {
    if (activeEmployeeId) {
      setInputEmployeeId(activeEmployeeId);
    }
  }, [activeEmployeeId]);
  const isImpersonating = Boolean(isSuperAdmin && impersonatedEmail);
  const activeName = user?.userDetails || 'Guest';
  const isLoginRoute = location.pathname === '/login';
  const canAccessUsersManagement = (user?.userRoles || []).some((role) => ['admin', 'kcs-super-user'].includes((role || '').toLowerCase()));
  const canProvisionProviders = (user?.userRoles || []).some((role) => (role || '').toLowerCase() === 'kcs-super-user');
  const handleSignOut = () => {
    if (hasRealSession) {
      window.location.href = logoutUrl;
      return;
    }

    signOutLocalDevelopment();
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  const navItems = [
    { path: '/provider/dashboard', label: 'Dashboard', icon: House },
    ...(canAccessUsersManagement ? [{ path: '/provider/services', label: 'Services', icon: Broom }] : []),
    ...(canAccessUsersManagement ? [{ path: '/provider/users', label: 'Users', icon: Users }] : []),
    { path: '/provider/schedule', label: 'Schedule', icon: Clock },
    { path: '/provider/calendar', label: 'Calendar', icon: Calendar },
    { path: '/provider/help', label: 'Help', icon: Question },
    ...(canProvisionProviders ? [{ path: '/provider/provisioning', label: 'Provision Provider', icon: Buildings }] : []),
  ];

  if (authLoading) {
    return (
      <div className="auth-loading">
        <div className="auth-card">Checking secure sign in...</div>
      </div>
    );
  }

  if (!user) {
    if (authError) return <div className="auth-loading"><div className="auth-card" role="alert">{authError} <button type="button" onClick={refreshAuth}>Retry</button></div></div>;
    if (!isLoginRoute) {
      return <Navigate to="/login" replace />;
    }

    return (
      <main className="auth-shell">
        {children}
      </main>
    );
  }

  if (!isAllowedUser) {
    return (
      <div className="auth-loading">
        <div className="auth-card auth-blocked">
          <h2>Access not enabled</h2>
          <p>
            This MVP currently allows only phil.robinson@kirinari.com.au.
          </p>
          <p className="auth-blocked-user">
            Signed in as {realUser?.userDetails || 'unknown user'}
          </p>
          <button type="button" className="auth-link" onClick={handleSignOut}>Sign out</button>
        </div>
      </div>
    );
  }

  if (isLoginRoute) {
    return <Navigate to="/provider/dashboard" replace />;
  }

  return (
    <div className="layout">
      <button className="hamburger-button" onClick={toggleSidebar} aria-label="Toggle menu">
        <List size={24} weight="bold" />
      </button>
      
      <nav className={`sidebar ${sidebarOpen ? 'sidebar-open' : ''}`}>
        <button className="close-button" onClick={closeSidebar} aria-label="Close menu">
          <X size={24} weight="bold" />
        </button>
        <ul className="nav-menu">
          {navItems.map((item) => {
            const IconComponent = item.icon;
            return (
              <li key={item.path}>
                <Link
                  to={item.path}
                  className={location.pathname === item.path ? 'active' : ''}
                  onClick={closeSidebar}
                >
                  <span className="nav-icon">
                    <IconComponent size={24} weight="regular" />
                  </span>
                  <span className="nav-label">{item.label}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>
      <main className="main-content">
        <header className="top-header">
          <div className="header-branding">
            <h1 className="brand-title">KCS Connect+</h1>
            {providerName && <span className="header-provider-name">{providerName}</span>}
          </div>
          <div className="header-actions">
            {isSuperAdmin && !authLoading && (
              <form 
                className="provider-id-switcher"
                onSubmit={(e) => {
                  e.preventDefault();
                  switchEmployeeId(inputEmployeeId);
                }}
              >
                <label htmlFor="provider-id-input">Provider ID</label>
                <input
                  id="provider-id-input"
                  type="text"
                  className="provider-id-input"
                  value={inputEmployeeId}
                  onChange={(e) => setInputEmployeeId(e.target.value)}
                  placeholder="e.g. 167"
                  title="Override Provider ID"
                />
                <button type="submit" className="provider-apply-btn" title="Load Provider">
                  Apply
                </button>
              </form>
            )}

            {user ? (
              <div className="session-actions">
                <span className="session-user">{isImpersonating ? `Testing as ${activeName}` : activeName}</span>
                <button type="button" className="auth-link" onClick={handleSignOut}>Logout</button>
              </div>
            ) : (
              !authLoading && (
                <div className="session-actions">
                  <Link className="auth-link" to="/login">Sign in</Link>
                </div>
              )
            )}

            {user && (
              <Link
                to="/provider/workers/me/edit"
                className="profile-icon-button"
                title="Edit my details"
                aria-label="Edit my details"
              >
                <UserCircleIcon size={28} weight="regular" />
              </Link>
            )}
          </div>
        </header>
        {authError && <div role="alert">{authError} <button type="button" onClick={refreshAuth}>Retry</button></div>}
        {providerConfigured === false ? (
          <div className="provider-context-warning" role="alert">
            <h2>Provider not configured</h2>
            <p>Provider ID {activeEmployeeId} has no active KCS Connect provider record. Provider data is hidden until this provider is configured.</p>
          </div>
        ) : children}
        <span className="app-version">v{APP_VERSION}</span>
      </main>
    </div>
  );
}

export default Layout;
